import React, { useState } from 'react';
import { Grid, Paper, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { AccountData, OpenBalancesTab, OrderService } from '../api';
import { useAuth } from '../session/InternalAuthProvider';
import { todayInTimezone } from '../calendar/agendaView/AreasViewContext';
import { CARD_RADIUS } from '../calendar/agendaView/agendaStyles';
import { getDateFormat } from '../common/getDateAndTimeFormat';
import { mb } from '../common/theme/mbTokens';
import { billAge, BillAgeType } from './billAge';
import { OpenBillsTable } from './OpenBillsTable';
import { formatBillAmount } from './openBillUtils';
import { useOpenBalancesSummary } from './useOpenBalancesSummary';

const useBillsOverview = (tab: OpenBalancesTab) =>
  useSWR({ opId: 'getOpenBalancesOverview', tab }, () => OrderService.getOpenBalances(tab, 1, 1));

export const OpenBillsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const { mutateSummary } = useOpenBalancesSummary();
  const accountToday = todayInTimezone(account.timezone);
  const [tab, setTab] = useState(OpenBalancesTab.OPEN);

  const { data: openOverview, mutate: mutateOpenOverview } = useBillsOverview(OpenBalancesTab.OPEN);
  const { data: closedOverview, mutate: mutateClosedOverview } = useBillsOverview(OpenBalancesTab.CLOSED_WITH_NOTE);

  const refreshOverviews = () => {
    void mutateOpenOverview();
    void mutateClosedOverview();
    void mutateSummary();
  };

  const oldestText = (() => {
    if (!openOverview?.oldest_open_date) {
      return '-';
    }
    const age = billAge(openOverview.oldest_open_date, accountToday);
    return age.type === BillAgeType.TODAY
      ? t('open_bills.age_today')
      : t('open_bills.oldest_days', { count: age.days });
  })();
  const oldestDateText = openOverview?.oldest_open_date
    ? `${moment(openOverview.oldest_open_date).format('ddd')} ${moment(openOverview.oldest_open_date).format(
        shortDateFormat,
      )}`
    : '';

  const summaryBox = (label: string, value: React.ReactNode, valueColor?: string, caption?: string) => (
    <Grid item xs={4}>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: CARD_RADIUS,
          backgroundColor: 'mb.surface',
          paddingBlock: 2,
          paddingInline: 2.5,
        }}>
        <Typography sx={{ fontSize: 13, color: 'mb.muted' }}>{label}</Typography>
        <Typography
          component="div"
          sx={{ fontSize: 26, fontWeight: 700, color: valueColor, fontVariantNumeric: 'tabular-nums' }}>
          {value}
          {caption && (
            <Typography
              component="span"
              sx={{ fontSize: 14, fontWeight: 400, color: 'mb.muted', marginInlineStart: 1 }}>
              {caption}
            </Typography>
          )}
        </Typography>
      </Paper>
    </Grid>
  );

  const totalAmountText = formatBillAmount(openOverview?.total_amount ?? 0, account);

  const summary = isDesktop ? (
    <Grid container spacing={2}>
      {summaryBox(t('open_bills.total_open'), totalAmountText, 'mb.danger')}
      {summaryBox(t('open_bills.bills_count'), openOverview?.total ?? 0)}
      {summaryBox(t('open_bills.oldest'), oldestText, undefined, oldestDateText)}
    </Grid>
  ) : (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: CARD_RADIUS,
        backgroundColor: 'mb.surface',
        paddingBlock: 1.5,
        paddingInline: 1.75,
      }}>
      <Grid container alignItems="center" wrap="nowrap" gap={1}>
        <Grid item xs>
          <Typography sx={{ fontSize: 13, color: 'mb.muted' }}>{t('open_bills.total_open')}</Typography>
          <Typography sx={{ fontSize: 14 }}>
            <Typography component="span" sx={{ fontWeight: 700 }}>
              {openOverview?.total ?? 0} {t('open_bills.bills_count')}
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, color: 'mb.muted' }}>
              {' · '}
              {t('open_bills.oldest')} {oldestText}
            </Typography>
          </Typography>
        </Grid>
        <Typography sx={{ fontSize: 24, fontWeight: 700, color: 'mb.danger', fontVariantNumeric: 'tabular-nums' }}>
          {totalAmountText}
        </Typography>
      </Grid>
    </Paper>
  );

  const tabLabel = (key: string, total?: number) => `${t(key)} ${total ?? ''}`.trim();

  return (
    <Grid container direction="column" wrap="nowrap" sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <HelmetProvider>
        <Helmet>
          <title>Monkeybook - {t('main_sidebar.open_bills')}</title>
        </Helmet>
      </HelmetProvider>

      <Grid
        container
        direction="column"
        wrap="nowrap"
        gap={2}
        sx={{ padding: mb.layout.cardPadding, paddingBottom: 0 }}>
        <Grid item>
          <Typography component="h1" sx={{ fontSize: { xs: 22, md: 30 }, fontWeight: 700, lineHeight: 1.2 }}>
            {t('open_bills.title')}
          </Typography>
          <Typography sx={{ marginTop: 0.5, color: 'mb.muted' }}>{t('open_bills.subtitle')}</Typography>
        </Grid>

        {summary}

        <Tabs
          value={tab}
          onChange={(_event, newTab: OpenBalancesTab) => setTab(newTab)}
          variant={isDesktop ? 'standard' : 'fullWidth'}>
          <Tab value={OpenBalancesTab.OPEN} label={tabLabel('open_bills.tab_open', openOverview?.total)} />
          <Tab
            value={OpenBalancesTab.CLOSED_WITH_NOTE}
            label={tabLabel('open_bills.tab_closed_with_note', closedOverview?.total)}
          />
        </Tabs>
      </Grid>

      <OpenBillsTable key={tab} tab={tab} accountToday={accountToday} onBillsChanged={refreshOverviews} />
    </Grid>
  );
};
